import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { ContentI18n } from "@/lib/questContent/types";
import { parseIssuerKind } from "@/lib/certificates/issuer";
import { ReviewConflictError, normalizeReview, orderTags, type CertificateReview } from "@/lib/certificates/review";
import type {
  CertificateDetail,
  MyCertificate,
  PendingReview,
  PublicCertificate,
  TaskCertificate,
} from "@/lib/certificates/types";
import type { PublicSkillCertification, SkillCertification } from "@/lib/skillCertifications/types";
import type { ICertificateService } from "@/services/interfaces/ICertificateService";

/** The public fields, as get_certificate / get_public_certificates_for_handle / the table name them. */
interface PublicRow {
  code: string;
  task_title: string;
  task_source_lang: string | null;
  task_i18n: ContentI18n | null;
  quest_title: string | null;
  quest_source_lang: string | null;
  quest_i18n: ContentI18n | null;
  issuer_name: string;
  /** Absent from a server older than 20261020150000. */
  issuer_kind?: string | null;
  approver_name: string | null;
  skills: string[] | null;
  is_volunteer: boolean;
  completed_at: string;
}

interface DetailRow extends PublicRow {
  worker_name: string;
  creator_note: string | null;
  note_author_name: string | null;
  note_i18n?: Record<string, string> | null;
  note_source_lang?: string | null;
  /** Absent from a server older than 20261021120000; null below 3 for visitors. */
  creator_rating?: number | null;
  creator_tags?: string[] | null;
  revoked_at: string | null;
  is_owner: boolean;
  can_edit_note: boolean;
  id?: string;
  task_id?: string | null;
  quest_id?: string | null;
  shown_on_profile?: boolean;
  review_rating?: number | null;
  review_editable_until?: string | null;
  review_version?: number;
}

interface MineRow extends PublicRow {
  id: string;
  task_id: string | null;
  shown_on_profile: boolean;
  revoked_at: string | null;
}

/** One element of get_pending_certificate_reviews' array (20261021170000). */
interface PendingRow {
  id: string;
  code: string;
  worker_name: string;
  task_id: string | null;
  quest_id: string | null;
  task_title: string;
  task_source_lang: string | null;
  task_i18n: ContentI18n | null;
  quest_title: string | null;
  quest_source_lang: string | null;
  quest_i18n: ContentI18n | null;
  completed_at: string;
  is_volunteer: boolean;
}

/** One element of get_task_certificates' array. */
interface TaskRow {
  id: string;
  code: string;
  worker_name: string;
  revoked_at: string | null;
  creator_rating: number | null;
  creator_tags: string[] | null;
  creator_note: string | null;
  review_editable_until: string | null;
  review_version: number;
  can_edit: boolean;
}

function toPublic(r: PublicRow): PublicCertificate {
  return {
    code: r.code,
    taskTitle: r.task_title,
    taskSourceLang: r.task_source_lang,
    taskI18n: r.task_i18n ?? {},
    questTitle: r.quest_title,
    questSourceLang: r.quest_source_lang,
    questI18n: r.quest_i18n ?? {},
    issuerName: r.issuer_name,
    issuerKind: parseIssuerKind(r.issuer_kind),
    approverName: r.approver_name ?? null,
    skills: r.skills ?? [],
    isVolunteer: r.is_volunteer,
    completedAt: r.completed_at,
  };
}

const MINE_COLUMNS =
  "id, task_id, code, task_title, task_source_lang, task_i18n, quest_title, quest_source_lang, quest_i18n, " +
  "issuer_name, issuer_kind, approver_name, skills, is_volunteer, completed_at, shown_on_profile, revoked_at";

export class SupabaseCertificateService implements ICertificateService {
  async getByCode(code: string): Promise<CertificateDetail | null> {
    const { data, error } = await supabase.rpc("get_certificate", { p_code: code });
    if (error) throw new Error(error.message);
    if (!data) return null;
    const r = data as DetailRow;
    return {
      ...toPublic(r),
      workerName: r.worker_name,
      creatorNote: r.creator_note,
      noteAuthorName: r.note_author_name,
      noteI18n: (r.note_i18n as Record<string, string> | null) ?? {},
      noteSourceLang: r.note_source_lang ?? null,
      creatorRating: r.creator_rating ?? null,
      creatorTags: orderTags(r.creator_tags),
      revokedAt: r.revoked_at,
      isOwner: r.is_owner === true,
      canEditNote: r.can_edit_note === true,
      id: r.id,
      taskId: r.task_id,
      questId: r.quest_id,
      shownOnProfile: r.shown_on_profile,
      reviewRating: r.review_rating,
      reviewEditableUntil: r.review_editable_until,
      reviewVersion: r.review_version,
    };
  }

  // RLS: the holder reads their own rows.
  async listMine(userId: string): Promise<MyCertificate[]> {
    const { data, error } = await supabase
      .from("certificates")
      .select(MINE_COLUMNS)
      .eq("worker_id", userId)
      .order("completed_at", { ascending: false });
    if (error) throw new Error(error.message);
    return ((data ?? []) as unknown as MineRow[]).map((r) => ({
      ...toPublic(r),
      id: r.id,
      taskId: r.task_id,
      shownOnProfile: r.shown_on_profile,
      revokedAt: r.revoked_at,
    }));
  }

  async listPublicForHandle(handle: string, profileId: string): Promise<PublicCertificate[]> {
    const { data, error } = await supabase.rpc("get_public_certificates_for_handle", {
      p_handle: handle,
      p_profile_id: profileId,
    });
    if (error) throw new Error(error.message);
    return ((data ?? []) as PublicRow[]).map(toPublic);
  }

  // RLS: the holder reads their own rows.
  async getMyCodeForTask(taskId: string, userId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("certificates")
      .select("code")
      .eq("task_id", taskId)
      .eq("worker_id", userId)
      .is("revoked_at", null)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as { code: string } | null)?.code ?? null;
  }

  // RLS: the quest creator reads certificates on their quests.
  async getIdForSubmission(submissionId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("certificates")
      .select("id")
      .eq("submission_id", submissionId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as { id: string } | null)?.id ?? null;
  }

  // A definer RPC rather than the table, so can_edit comes from the database clock.
  async listForTask(taskId: string): Promise<TaskCertificate[]> {
    const { data, error } = await supabase.rpc("get_task_certificates", { p_task_id: taskId });
    if (error) throw new Error(error.message);
    return ((data ?? []) as TaskRow[]).map((r) => ({
      id: r.id,
      code: r.code,
      workerName: r.worker_name,
      revokedAt: r.revoked_at,
      review: { rating: r.creator_rating, tags: orderTags(r.creator_tags), note: r.creator_note ?? "" },
      reviewEditableUntil: r.review_editable_until,
      canEdit: r.can_edit === true,
      reviewVersion: r.review_version,
    }));
  }

  async listPendingReviews(): Promise<PendingReview[]> {
    const { data, error } = await supabase.rpc("get_pending_certificate_reviews");
    if (error) throw new Error(error.message);
    return ((data ?? []) as PendingRow[]).map((r) => ({
      id: r.id,
      code: r.code,
      workerName: r.worker_name,
      taskId: r.task_id,
      questId: r.quest_id,
      taskTitle: r.task_title,
      taskSourceLang: r.task_source_lang,
      taskI18n: r.task_i18n ?? {},
      questTitle: r.quest_title,
      questSourceLang: r.quest_source_lang,
      questI18n: r.quest_i18n ?? {},
      completedAt: r.completed_at,
      isVolunteer: r.is_volunteer,
    }));
  }

  async setReview(certificateId: string, review: CertificateReview, expectedVersion: number | null): Promise<void> {
    const r = normalizeReview(review);
    const { error } = await supabase.rpc("set_certificate_review", {
      p_certificate_id: certificateId,
      p_rating: r.rating,
      p_tags: r.tags,
      p_note: r.note,
      p_expected_version: expectedVersion,
    });
    if (error?.code === "40001") throw new ReviewConflictError();
    if (error) throw new Error(error.message);
    // Fire-and-forget: translate the note so it renders in the reader's language.
    if (r.note?.trim()) {
      void this.translateNote(certificateId);
    }
  }

  private translateNote(certificateId: string): Promise<void> {
    return callEdgeFunction("translate-certificate-note", { body: { certificateId } }).then(
      () => undefined,
      (e) => { console.warn("[DEV-note-i18n] note translation request failed", certificateId, e); },
    );
  }

  async setShown(certificateId: string, shown: boolean): Promise<void> {
    const { error } = await supabase.rpc("set_certificate_shown", {
      p_certificate_id: certificateId,
      p_shown: shown,
    });
    if (error) throw new Error(error.message);
  }

  // ── Skill Certifications (20261028140000) ──────────────────────────────

  async listSkillCertifications(profileId: string): Promise<SkillCertification[]> {
    const { data, error } = await supabase.rpc("get_skill_certifications", { p_profile_id: profileId });
    if (error) throw new Error(error.message);
    return ((data ?? []) as SkillCertificationRow[]).map(toSkillCertification);
  }

  async listPublicSkillCertifications(handle: string, profileId: string): Promise<PublicSkillCertification[]> {
    const { data, error } = await supabase.rpc("get_public_skill_certifications_for_handle", {
      p_handle: handle,
      p_profile_id: profileId,
    });
    if (error) throw new Error(error.message);
    return ((data ?? []) as PublicSkillCertificationRow[]).map(toPublicSkillCertification);
  }
}

// ── Skill certification row shapes ────────────────────────────────────────

interface SkillCertificationRow {
  skill: string;
  skill_key: string;
  certified_at: string;
  unique_creators: number;
}

interface PublicSkillCertificationRow {
  skill: string;
  skill_key: string;
  certified_at: string;
}

function toSkillCertification(r: SkillCertificationRow): SkillCertification {
  return {
    skill: r.skill,
    skillKey: r.skill_key,
    certifiedAt: r.certified_at,
    uniqueCreators: r.unique_creators,
  };
}

function toPublicSkillCertification(r: PublicSkillCertificationRow): PublicSkillCertification {
  return {
    skill: r.skill,
    skillKey: r.skill_key,
    certifiedAt: r.certified_at,
  };
}
